import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useCreateMenuItem, useUpdateMenuItem, type MenuItemRow } from "@/hooks/useMenuItems";
import { AlertTriangle, CheckCircle2, FileUp } from "lucide-react";

interface ParsedRow {
  name: string;
  description: string;
  category_name: string;
  pricing_type: string;
  price: number;
  label: string;
  serves: number | null;
  sku: string;
  abbreviation: string;
  dietary_tags: string[];
  is_active: boolean;
  error?: string;
  isUpdate?: boolean;
  existingId?: string;
}

function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let current = "";
  let inQuotes = false;
  let row: string[] = [];

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        row.push(current.trim());
        current = "";
      } else if (ch === "\n" || (ch === "\r" && text[i + 1] === "\n")) {
        row.push(current.trim());
        if (row.some((c) => c !== "")) rows.push(row);
        row = [];
        current = "";
        if (ch === "\r") i++;
      } else {
        current += ch;
      }
    }
  }
  row.push(current.trim());
  if (row.some((c) => c !== "")) rows.push(row);
  return rows;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingItems: MenuItemRow[];
  categories: { id: string; name: string }[];
}

export function MenuItemImportDialog({ open, onOpenChange, existingItems, categories }: Props) {
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [failedRows, setFailedRows] = useState<{ name: string; message: string }[]>([]);
  // Bumped on close, so an import still running when the dialog closes can't
  // write its progress or result onto the reset dialog.
  const importRun = useRef(0);
  const createItem = useCreateMenuItem();
  const updateItem = useUpdateMenuItem();

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const rows = parseCSV(text);
      if (rows.length < 2) return;

      const header = rows[0].map((h) => h.toLowerCase().replace(/\s+/g, "_"));
      const nameIdx = header.indexOf("name");
      const descIdx = header.indexOf("description");
      const catIdx = header.indexOf("category");
      const ptIdx = header.indexOf("pricing_type");
      const priceIdx = header.indexOf("price");
      const labelIdx = header.indexOf("label");
      const servesIdx = header.indexOf("serves");
      const skuIdx = header.indexOf("sku");
      const abbrIdx = header.indexOf("abbreviation");
      const tagsIdx = header.indexOf("dietary_tags");
      const activeIdx = header.indexOf("active");

      const parsed: ParsedRow[] = rows.slice(1).map((row) => {
        const get = (idx: number) => (idx >= 0 && idx < row.length ? row[idx] : "");
        const name = get(nameIdx);
        const categoryName = get(catIdx);
        const priceStr = get(priceIdx);
        const price = parseFloat(priceStr) || 0;
        const servesStr = get(servesIdx);
        const serves = servesStr ? parseInt(servesStr) || null : null;
        const tagsStr = get(tagsIdx);
        const tags = tagsStr ? tagsStr.split(/[,;|]/).map((t) => t.trim().toLowerCase().replace(/[\s-]+/g, "_")).filter(Boolean) : [];
        const activeStr = get(activeIdx).toLowerCase();
        const isActive = activeStr === "" || activeStr === "true" || activeStr === "yes" || activeStr === "1";

        const existing = existingItems.find((i) => i.name.toLowerCase() === name.toLowerCase());
        const cat = categories.find((c) => c.name.toLowerCase() === categoryName.toLowerCase());

        let error: string | undefined;
        if (!name) error = "Missing name";
        else if (!cat && categoryName) error = `Unknown category: ${categoryName}`;
        else if (!cat && !categoryName && !existing) error = "Missing category";

        return {
          name,
          description: get(descIdx),
          category_name: categoryName,
          pricing_type: get(ptIdx) || "per_plate",
          price,
          label: get(labelIdx),
          serves,
          sku: get(skuIdx),
          abbreviation: get(abbrIdx),
          dietary_tags: tags,
          is_active: isActive,
          error,
          isUpdate: !!existing,
          existingId: existing?.id,
        };
      });

      setParsedRows(parsed);
      setDone(false);
      setProgress(0);
      setSavedCount(0);
      setFailedRows([]);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const validRows = parsedRows.filter((r) => !r.error);
  const errorRows = parsedRows.filter((r) => r.error);
  const newRows = validRows.filter((r) => !r.isUpdate);
  const updateRows = validRows.filter((r) => r.isUpdate);

  // A failed row doesn't stop the import: the rest still save, and the done
  // view lists what failed. The hooks' onError shows a toast for each failure.
  const handleImport = async () => {
    const run = ++importRun.current;
    setImporting(true);
    setProgress(0);
    let saved = 0;
    const failed: { name: string; message: string }[] = [];
    try {
      for (let i = 0; i < validRows.length; i++) {
        const row = validRows[i];
        const cat = categories.find((c) => c.name.toLowerCase() === row.category_name.toLowerCase());

        try {
          if (row.isUpdate && row.existingId) {
            await updateItem.mutateAsync({
              id: row.existingId,
              name: row.name,
              description: row.description || undefined,
              category_id: cat?.id,
              sku: row.sku || undefined,
              abbreviation: row.abbreviation || undefined,
              dietary_tags: row.dietary_tags,
              is_active: row.is_active,
              prices: row.price ? [{ pricing_type: row.pricing_type, price: row.price, label: row.label || undefined, serves: row.serves ?? undefined }] : undefined,
            });
          } else if (cat) {
            await createItem.mutateAsync({
              name: row.name,
              description: row.description || undefined,
              category_id: cat.id,
              sku: row.sku || undefined,
              abbreviation: row.abbreviation || undefined,
              dietary_tags: row.dietary_tags,
              is_active: row.is_active,
              prices: row.price ? [{ pricing_type: row.pricing_type, price: row.price, label: row.label || undefined, serves: row.serves ?? undefined }] : undefined,
            });
          } else {
            throw new Error(`Unknown category: ${row.category_name}`);
          }
          saved++;
        } catch (err) {
          // The hooks throw supabase's PostgrestError, a plain object rather than an Error.
          failed.push({ name: row.name, message: (err as { message?: string } | null)?.message || "Save failed" });
        }
        if (importRun.current === run) setProgress(Math.round(((i + 1) / validRows.length) * 100));
      }
    } finally {
      if (importRun.current === run) {
        setSavedCount(saved);
        setFailedRows(failed);
        setImporting(false);
        setDone(true);
      }
    }
  };

  const handleClose = (v: boolean) => {
    if (!v) {
      importRun.current++;
      setImporting(false);
      setParsedRows([]);
      setDone(false);
      setProgress(0);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import Menu Items</DialogTitle>
        </DialogHeader>

        {parsedRows.length === 0 && !done && (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV file with columns: Name, Description, Category, Pricing Type, Price, Label, Serves, SKU, Abbreviation, Dietary Tags, Active.
            </p>
            <label className="flex flex-col items-center gap-2 border-2 border-dashed rounded-lg p-8 cursor-pointer hover:border-primary transition-colors">
              <FileUp className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">Choose CSV file</span>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
            </label>
          </div>
        )}

        {parsedRows.length > 0 && !done && (
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline">{validRows.length} valid</Badge>
              <Badge variant="secondary">{newRows.length} new</Badge>
              <Badge variant="secondary">{updateRows.length} updates</Badge>
              {errorRows.length > 0 && (
                <Badge variant="destructive">{errorRows.length} errors</Badge>
              )}
            </div>
            {errorRows.length > 0 && (
              <div className="max-h-32 overflow-auto text-xs space-y-1 bg-destructive/5 rounded p-2">
                {errorRows.slice(0, 10).map((r, i) => (
                  <div key={i} className="flex items-start gap-1">
                    <AlertTriangle className="h-3 w-3 text-destructive shrink-0 mt-0.5" />
                    <span><strong>{r.name || `Row ${i + 1}`}</strong>: {r.error}</span>
                  </div>
                ))}
                {errorRows.length > 10 && <p className="text-muted-foreground">...and {errorRows.length - 10} more</p>}
              </div>
            )}
            {importing && <Progress value={progress} className="h-2" />}
          </div>
        )}

        {done && failedRows.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-6">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
            <p className="font-medium">Import complete!</p>
            <p className="text-sm text-muted-foreground">{savedCount} items imported successfully.</p>
          </div>
        )}

        {done && failedRows.length > 0 && (
          <div className="space-y-3 py-2">
            <div className="flex flex-col items-center gap-2 pt-4">
              <AlertTriangle className="h-10 w-10 text-destructive" />
              <p className="font-medium">Import finished with errors</p>
              <p className="text-sm text-muted-foreground">
                {savedCount} of {validRows.length} items saved. These rows failed:
              </p>
            </div>
            <div className="max-h-32 overflow-auto text-xs space-y-1 bg-destructive/5 rounded p-2">
              {failedRows.map((r, i) => (
                <div key={i} className="flex items-start gap-1">
                  <AlertTriangle className="h-3 w-3 text-destructive shrink-0 mt-0.5" />
                  <span><strong>{r.name}</strong>: {r.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {!done && parsedRows.length > 0 && (
            <>
              <Button variant="outline" onClick={() => setParsedRows([])} disabled={importing}>Back</Button>
              <Button onClick={handleImport} disabled={importing || validRows.length === 0}>
                {importing ? `Importing... ${progress}%` : `Import ${validRows.length} items`}
              </Button>
            </>
          )}
          {done && <Button onClick={() => handleClose(false)}>Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
